import React from "react";
import SuiBox from "components/SuiBox";
import Select, { components as selectComponents } from "react-select";

const MENU_PORTAL_Z_INDEX = 9999;

function Createable({
  options,
  placeholder,
  style,
  inputStyle,
  onChange,
  value,
  disabled,
  loading,
  isMulti,
  closeMenuOnSelect,
  hideSelectedOptions,
  components: componentsProp,
  defaultValue,
  isClearable = false,
  isSearchable = true,
  autoComplete = "off",
  name,
  inputId,
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
  openMenuOnClick,
  openMenuOnFocus = false,
  menuPortalTarget = typeof document !== "undefined" ? document.body : undefined,
  menuPosition = "fixed",
}) {
  // Portal to body so dropdown menu is not clipped by parent overflow (e.g. modals)
  const usePortal = Boolean(menuPortalTarget);
  const customStyles = {
    control: (styles, state) => ({
      ...styles,
      borderRadius: "0.5rem",
      fontSize: 14,
      ...style,
      border: state.isFocused
        ? "1px solid #35d1f5"
        : state.isDisabled && "0.0625rem solid #d2d6da",
      boxShadow: state.isFocused && "0 0 0 1px #35d1f5",
      backgroundColor: "white",
      "&:hover": {
        borderColor: state.isFocused ? "#35d1f5" : "none",
      },
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        fontSize: 14,
        lineHeight: "15px",
        whiteSpace: "initial !important",
        zIndex: 10000,
      };
    },
    input: (styles) => ({
      ...styles,
      height: "1.800rem",
      ...inputStyle,
    }),
    placeholder: (styles) => ({
      ...styles,
      fontSize: "0.875rem !important",
      paddingBottom: 4,
      color: "#8392ab",
      paddingLeft: 4,
      paddingTop: 4,
    }),
    singleValue: (styles, { data }) => ({
      ...styles,
      fontSize: 13,
      color: "#495057!important",
    }),
    menu: (styles) => ({
      ...styles,
      zIndex: usePortal ? MENU_PORTAL_Z_INDEX : 2,
    }),
    menuPortal: (styles) => ({
      ...styles,
      zIndex: MENU_PORTAL_Z_INDEX,
    }),
  };

  const BaseInput = componentsProp?.Input ?? selectComponents.Input;

  /**
   * Memoised so the search input keeps the same component identity between renders.
   * A new identity makes React remount the <input>, which drops focus and the typed
   * text whenever a parent re-renders (e.g. form state updates while the user types).
   */
  const SearchInput = React.useMemo(
    () =>
      function SelectSearchInput(inputProps) {
        return (
          <BaseInput
            {...inputProps}
            autoComplete={autoComplete}
            {...(name ? { name } : {})}
          />
        );
      },
    [BaseInput, autoComplete, name]
  );

  const mergedComponents = React.useMemo(
    () => ({ ...componentsProp, Input: SearchInput }),
    [componentsProp, SearchInput]
  );

  return (
    <SuiBox>
      <Select
        className="basic-single"
        classNamePrefix="select"
        onChange={onChange}
        style={{ zIndex: 2 }}
        placeholder={placeholder}
        styles={customStyles}
        value={value}
        isClearable={isClearable}
        isDisabled={disabled}
        options={options}
        isLoading={loading ?? false}
        isMulti={isMulti}
        closeMenuOnSelect={closeMenuOnSelect}
        hideSelectedOptions={hideSelectedOptions}
        components={mergedComponents}
        defaultValue={defaultValue}
        inputId={inputId}
        isSearchable={isSearchable}
        menuIsOpen={menuIsOpen}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        openMenuOnClick={openMenuOnClick}
        openMenuOnFocus={openMenuOnFocus}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
      />
    </SuiBox>
  );
}

export default Createable;
